import { useState, useEffect } from 'react';
import { Spin, message } from 'antd';
import api from '@common/api';
import { getUserLoginStatus } from '../../../services';
import { reportPageView } from '../../../services/reportLog';
import { useUserStore } from '../../../store';
import './index.scss';

interface LoginModalProps {
  onClose: () => void;
  onLoginSuccess: () => void;
}

// 从 window 读取 OAuth 配置（由 EJS 模板注入）
const getOAuthConfig = () => {
  const config = (window as any).__OAUTH_CONFIG__ || {};
  return {
    googleClientId: config.googleClientId || '',
    facebookAppId: config.facebookAppId || '',
  };
};

// 获取当前域名并拼接路径
const getUrlWithCurrentDomain = (path: string) => {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;
  const baseUrl = [protocol, '//', hostname, port ? `:${port}` : ''].join('');
  return [baseUrl, path].join('');
};

const privacyUrl = 'https://phoenix-browser.com/privacy-policy.html';
const termsUrl = getUrlWithCurrentDomain('/terms.html');

// Google Icon SVG paths
const googleIconPaths = {
  p1: "M22.56 12.25C22.56 11.47 22.49 10.72 22.36 10H12V14.255H17.92C17.665 15.63 16.89 16.795 15.725 17.575V20.335H19.28C21.36 18.42 22.56 15.6 22.56 12.25Z",
  p2: "M12 23C14.97 23 17.46 22.015 19.28 20.335L15.725 17.575C14.74 18.235 13.48 18.625 12 18.625C9.135 18.625 6.71 16.69 5.845 14.09H2.17V16.94C3.98 20.535 7.7 23 12 23Z",
  p3: "M5.845 14.09C5.625 13.43 5.5 12.725 5.5 12C5.5 11.275 5.625 10.57 5.845 9.91V7.06H2.17C1.4 8.595 0.96 10.345 0.96 12.1C0.96 13.855 1.4 15.59 2.17 17.125L5.845 14.09Z",
  p4: "M12 5.375C13.615 5.375 15.065 5.93 16.205 7.02L19.36 3.865C17.455 2.09 14.965 1 12 1C7.7 1 3.98 3.465 2.17 7.06L5.845 9.91C6.71 7.31 9.135 5.375 12 5.375Z",
};

// Facebook Icon SVG path
const facebookIconPath = "M23.998 12C23.998 5.372 18.626 0 11.999 0C5.372 0 0 5.372 0 12C0 17.988 4.388 22.952 10.124 23.852V15.468H7.078V11.999H10.124V9.356C10.124 6.348 11.916 4.687 14.656 4.687C15.969 4.687 17.342 4.921 17.342 4.921V7.874H15.83C14.34 7.874 13.875 8.799 13.875 9.748V12H17.203L16.671 15.469H13.875V23.853C19.611 22.953 23.998 17.989 23.998 12Z";

// 初始化 Google OAuth
const initGoogleCodeClient = (clientId: string) => {
  if (!(window as any).google?.accounts?.oauth2 || !clientId) return false;
  
  try {
    const googleCodeClient = (window as any).google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: 'openid email profile',
      ux_mode: 'popup',
    });

    (window as any).googleCodeClient = googleCodeClient;
    return true;
  } catch (error) {
    console.error('Failed to init Google OAuth:', error);
    return false;
  }
};

// 初始化 Facebook OAuth
const initFacebookCodeClient = (appId: string) => {
  if (!(window as any).FB || !appId) return false;
  
  try {
    (window as any).FB.init({
      appId: appId,
      xfbml: true,
      version: 'v19.0',
    });
    return true;
  } catch (error) {
    console.error('Failed to init Facebook OAuth:', error);
    return false;
  }
};

export default function LoginModal({ onClose, onLoginSuccess }: LoginModalProps) {
  const [loading, setLoading] = useState(false);
  const oauthConfig = getOAuthConfig();
  const setUser = useUserStore((state) => state.setUser);

  useEffect(() => {
    reportPageView('content_0005');
  }, []);

  // 初始化 OAuth SDK（SDK 由 EJS 模板在所有页面加载）
  useEffect(() => {
    // 初始化 Google
    if (oauthConfig.googleClientId) {
      // SDK 可能还在加载，等待就绪后初始化
      if ((window as any).google?.accounts?.oauth2) {
        initGoogleCodeClient(oauthConfig.googleClientId);
      } else {
        // 等待 SDK 加载完成
        const originalCallback = (window as any).onGoogleLibraryLoad;
        (window as any).onGoogleLibraryLoad = () => {
          originalCallback?.();
          initGoogleCodeClient(oauthConfig.googleClientId);
        };
      }
    }
    
    // 初始化 Facebook
    if (oauthConfig.facebookAppId) {
      if ((window as any).FB) {
        initFacebookCodeClient(oauthConfig.facebookAppId);
      } else {
        const originalCallback = (window as any).fbAsyncInit;
        (window as any).fbAsyncInit = () => {
          originalCallback?.();
          initFacebookCodeClient(oauthConfig.facebookAppId);
        };
      }
    }
  }, [oauthConfig.googleClientId, oauthConfig.facebookAppId]);

  // 处理登录成功后的用户状态检查
  async function checkUserAndCallback(uid: string, loginType: 1 | 2, userName?: string, userEmail?: string) {
    try {
      const res: any = await getUserLoginStatus({ uid, loginType });
      const data = res?.data || res;
      const isRegistered = data?.isRegistered === true;
      const userInfo = data?.userInfo;

      // 存储用户数据到 zustand (会自动持久化到 localStorage)
      setUser({
        id: uid,
        name: userInfo?.stageName || userName || 'Artist',
        email: userInfo?.email || userEmail || '',
        avatar: userInfo?.profilePicture || '',
        isRegistered,
        loginType,
        country: userInfo?.country || '',
        bio: userInfo?.bio || '',
        genres: userInfo?.musicGenre ? userInfo.musicGenre.split(',') : [],
      });
      
      onLoginSuccess();
    } catch (error) {
      console.error('Failed to check user status:', error);
      // 接口失败时，默认作为新用户
      setUser({
        id: uid,
        name: userName || 'Artist',
        email: userEmail || '',
        avatar: '',
        isRegistered: false,
        loginType,
      });
      onLoginSuccess();
    }
  }

  // 处理 Facebook 登录
  async function responseFacebook(response: { accessToken: string }) {
    try {
      const loginRes: any = await api.post('login/facebook', { code: response.accessToken });
      const uid = loginRes?.data?.uid || loginRes?.uid || '';
      // 后端返回的是 username，不是 name
      const userName = loginRes?.data?.username || loginRes?.data?.name || loginRes?.username || '';
      const userEmail = loginRes?.data?.email || loginRes?.email || '';
      
      message.success('Login successful');
      await checkUserAndCallback(uid, 2, userName, userEmail);
    } catch (error: any) {
      console.error('Auth error', error);
      message.error(`Facebook login error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  const handleGoogleLogin = async () => {
    console.log('handleGoogleLogin');
    // Mock 登录模式：直接模拟登录成功（灰度/生产环境设为 false）
    const USE_MOCK_LOGIN = false;
    
    if (USE_MOCK_LOGIN) {
      setLoading(true);
      
      const mockUid = 'google_test_uid_' + Date.now();
      const mockName = 'Test Artist';
      const mockEmail = 'testartist@gmail.com';
      
      message.success('Login successful (Mock)');
      await checkUserAndCallback(mockUid, 1, mockName, mockEmail);
      
      setLoading(false);
      return;
    }

    // 真实登录逻辑
    // 检查配置
    if (!oauthConfig.googleClientId) {
      message.warning('Google login is not configured');
      return;
    }

    // 检查 SDK 是否就绪
    if (!(window as any).googleCodeClient) {
      // 尝试重新初始化
      if ((window as any).google?.accounts?.oauth2) {
        initGoogleCodeClient(oauthConfig.googleClientId);
      } else {
        message.error('Google SDK is loading, please try again later.');
        return;
      }
    }

    setLoading(true);

    (window as any).googleCodeClient.callback = async (response: { code?: string; error?: string }) => {
      if (response.error) {
        message.error(`Google login cancelled`);
        setLoading(false);
        return;
      }

      const code = response?.code;
      if (!code) {
        setLoading(false);
        return;
      }

      try {
        const loginRes: any = await api.post('login/google', { code });
        const uid = loginRes?.data?.uid || loginRes?.uid || '';
        // 后端返回的是 username，不是 name
        const userName = loginRes?.data?.username || loginRes?.data?.name || loginRes?.username || '';
        const userEmail = loginRes?.data?.email || loginRes?.email || '';
        
        message.success('Login successful');
        await checkUserAndCallback(uid, 1, userName, userEmail);
      } catch (error: any) {
        message.error(`Google login error: ${error.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    (window as any).googleCodeClient?.requestCode();
  };

  function handleFacebookLogin() {
    // 检查配置
    if (!oauthConfig.facebookAppId) {
      message.warning('Facebook login is not configured');
      return;
    }

    // 检查 SDK 是否就绪
    if (!(window as any).FB) {
      message.error('Facebook SDK is loading, please try again later.');
      return;
    }

    // 确保 FB 已初始化
    if (!(window as any).FB.getLoginStatus) {
      initFacebookCodeClient(oauthConfig.facebookAppId);
    }

    try {
      setLoading(true);

      (window as any).FB.login((response: { authResponse?: { accessToken: string }; status?: string }) => {
        if (response.authResponse) {
          responseFacebook(response.authResponse);
        } else {
          message.error('Facebook login cancelled');
          setLoading(false);
        }
      }, { scope: 'email,public_profile' });
    } catch (error: any) {
      message.error(`Facebook login error: ${error.message || 'Unknown error'}`);
      setLoading(false);
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="login-modal-overlay" onClick={handleBackdropClick}>
      <div className="login-modal">
        <div className="login-modal-border" />
        
        {/* Close Button */}
        <button className="login-modal-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none">
            <path d="M13 1L1 13" stroke="rgba(255, 255, 255, 0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M1 1L13 13" stroke="rgba(255, 255, 255, 0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="login-modal-content">
          {/* Header */}
          <div className="login-modal-header">
            <h2 className="login-modal-title">Welcome to Phoenix Music</h2>
            <p className="login-modal-subtitle">Sign in to start your music journey</p>
          </div>

          {/* Buttons */}
          <Spin spinning={loading}>
            <div className="login-modal-buttons">
              <button 
                className="login-btn google-btn" 
                onClick={handleGoogleLogin}
                // disabled={!oauthConfig.googleClientId}
              >
                <svg className="login-btn-icon" viewBox="0 0 24 24" fill="none">
                  <path d={googleIconPaths.p1} fill="#4285F4" />
                  <path d={googleIconPaths.p2} fill="#34A853" />
                  <path d={googleIconPaths.p3} fill="#FBBC05" />
                  <path d={googleIconPaths.p4} fill="#EA4335" />
                </svg>
                <span>Continue with Google</span>
              </button>

              <button 
                className="login-btn facebook-btn" 
                onClick={handleFacebookLogin}
                disabled={!oauthConfig.facebookAppId}
              >
                <svg className="login-btn-icon" viewBox="0 0 24 24" fill="none">
                  <path d={facebookIconPath} fill="white" />
                </svg>
                <span>Continue with Facebook</span>
              </button>
            </div>
          </Spin>

          {/* Agreement */}
          <div className="login-modal-agreement">
            <p>
              By continuing, you agree to Phoenix's{' '}
              <a href={termsUrl} target="_blank" rel="noopener noreferrer">Terms of Service</a>
              {' '}and{' '}
              <a href={privacyUrl} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              {' '}We support original artists only.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
